import { useCreateOrUpdateCohort } from '@/hooks/queries/cohorts';
import { Cohort } from '@/types/cohort';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
} from '@nextui-org/react';
import { useEffect, useState } from 'react';

interface CohortModalProps {
  isOpen: boolean;
  onClose: () => void;
  cohort?: Cohort | null;
}

export default function CohortModal({
  isOpen,
  onClose,
  cohort = null,
}: CohortModalProps) {
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const isEdit = Boolean(cohort?.id);

  const { mutate: saveCohort, isPending } = useCreateOrUpdateCohort({
    onSuccess: onClose,
    successMessage: isEdit
      ? 'Cohort updated successfully'
      : 'Cohort created successfully',
  });

  useEffect(() => {
    if (isOpen) {
      setName(cohort?.name ?? '');
      setIsDefault(cohort?.is_default ?? false);
    }
  }, [isOpen, cohort]);

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    saveCohort({
      id: cohort?.id,
      data: { name: trimmedName, is_default: isDefault },
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} placement="center">
      <ModalContent>
        <ModalHeader>{isEdit ? 'Edit Cohort' : 'Add Cohort'}</ModalHeader>
        <ModalBody className="gap-4">
          <Input
            label="Name"
            placeholder="Enter cohort name"
            value={name}
            onValueChange={setName}
            isRequired
          />
          <div className="flex items-center justify-between rounded-lg border border-divider p-4">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Default cohort</span>
              <span className="text-sm text-default-500">
                New SSO users are placed in this cohort on first sign-in (tenant
                match only).
              </span>
            </div>
            <Switch
              isSelected={isDefault}
              onValueChange={setIsDefault}
              color="primary"
              aria-label="Default cohort"
            />
          </div>
        </ModalBody>
        <ModalFooter className="justify-between border-t mt-4">
          <Button variant="bordered" onPress={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isLoading={isPending}
            isDisabled={!name.trim()}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
